import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { PATHS } from '@shared/constants/paths';
import { Observable, combineLatest, map } from 'rxjs';
import { UserService } from '@app/user/services';
import { PanAppState } from '@panamax/app-state';

/**
 * Functional route guard for super user status.
 * Defaults to guarding against super users
 *
 * @param redirectPath Path to redirect to if guarded
 * @param superUserOnly Will only allow super users for guarded route. Default: false
 * @param features Array of feature flags to check for, allows access if any are enabled
 * @returns true or UrlTree for redirect path
 */
export function superUserGuard(
  redirectPath: string = PATHS.HOME,
  superUserOnly: boolean = false,
  features: string[] = [],
): CanActivateFn {
  return (): Observable<boolean | UrlTree> => {
    const router = inject(Router);
    const userService = inject(UserService);
    const panAppState = inject(PanAppState);

    return combineLatest([
      userService.isSuperUser$(),
      panAppState.feature$(features),
    ]).pipe(
      map(([superUser, featureFlag]) => {
        if (featureFlag) {
          return true;
        } else if (superUserOnly) {
          return superUser ? true : router.parseUrl(redirectPath);
        } else {
          return superUser ? router.parseUrl(redirectPath) : true;
        }
      }),
    );
  };
}
